/**
 * identity-service `?code=` bootstrap — runs the front-door code→JWT exchange
 * BEFORE the React auth tree (`AuthProvider`) mounts.
 *
 * Story: authz-001-7a (WS-A login activation).
 *
 * ## Why this runs before AuthProvider (the fix this module exists for)
 *
 * The hosted login front-door bounces the member back to `<origin>?code=<one-time>`.
 * The identity-service JWT store (`identity-jwt-store`) is **in-memory only** and is
 * empty on a fresh page load. `@hello-world-dao/auth`'s `AuthProvider` resolves
 * auth state **once at mount** (`resolveUserFromJwt(getIdentityJwt)` → cookie
 * `checkSession` fallback) and does NOT re-derive when a JWT is stored later.
 *
 * If the code→JWT exchange runs as a component *inside* `AuthProvider` (the original
 * `IdentityCodeReturnHandler` design), two defects compound on the `?code=` landing:
 *   1. `AuthProvider` mounts with an empty JWT store → resolves "unauthenticated" →
 *      `ProtectedRoute` issues a full-document external redirect to the login
 *      front-door, which **aborts the in-flight `/token/exchange` fetch** (observed
 *      as `net::ERR_ABORTED`) → infinite login bounce; AC6 can never pass.
 *   2. Even without the abort, the JWT is set asynchronously after `AuthProvider`
 *      already resolved — so `isAuthenticated` stays false.
 *
 * Running the exchange first — and gating `AuthProvider` behind it (App renders a
 * loader while the bootstrap promise is pending) — means the JWT is
 * already in the store when `AuthProvider` mounts, so `resolveUserFromJwt` derives
 * the authed state on the first pass and no `ProtectedRoute` exists yet to abort the
 * exchange. No `@hello-world-dao/auth` change is required.
 *
 * ## Single-flight under StrictMode
 *
 * The exchange is a module-singleton cached promise: React StrictMode's
 * double-invoked effects (and any re-render) await the SAME promise, so the
 * single-use code is exchanged exactly once and its result is always applied. This
 * replaces the per-code guard the old in-tree handler needed.
 *
 * On exchange failure the code is still stripped from the URL and the app proceeds
 * unauthenticated — `-7b` retired the oracle-bridge cookie fallback (auth@3.0.0), so
 * the identity-service JWT is the sole gate; a failed exchange means no JWT, and the
 * member is treated as signed-out (no cookie session to fall back to).
 *
 * ## Silent refresh on plain reload (session-rehydrate fix)
 *
 * The `?code=` exchange only fires on the login *return*. On a plain page refresh
 * there is no `?code=`, the in-memory JWT store is empty (it never survives a
 * reload), and `AuthProvider` would resolve "unauthenticated" — logging the member
 * out on every refresh even though the httpOnly `hwdao_refresh` cookie
 * (Max-Age 14d) is still valid.
 *
 * The bootstrap therefore handles BOTH entry paths under the same single-flight
 * promise:
 *   - `?code=` present → exchange the one-time code (login return).
 *   - no `?code=` → silent {@link refreshIdentityJwt} (POST `/token/refresh`,
 *     credentialed, no body). The cookie carries the refresh token; on success the
 *     fresh JWT is stored (session restored) and identity-service rotates the
 *     cookie (the browser auto-applies the `Set-Cookie`). On failure (no / expired
 *     / revoked cookie → 401/error) the app proceeds unauthenticated, exactly as a
 *     genuine logged-out load — same outcome as before this fix.
 *
 * Because both paths live inside the SAME single-flight promise that `App` awaits
 * before mounting `AuthProvider`, the rehydrate settles before
 * `resolveUserFromJwt(getIdentityJwt)` reads the store on the first resolve pass.
 */

import { setIdentityJwt } from './identity-jwt-store';
import {
  CODE_PARAM,
  exchangeCodeForJwt,
  refreshIdentityJwtBounded,
  takeDeepLinkReturnPath,
} from './identity-service-client';

/**
 * The verdict the bootstrap resolves to (bl-977). `App` renders on this:
 *   - `authed` — a JWT was stored (exchange or silent refresh succeeded). Mount
 *     the auth tree authenticated.
 *   - `unauthenticated` — a DEFINITIVE signed-out state (401 refresh / failed
 *     exchange). Mount the auth tree → ProtectedRoute redirects to the login shell.
 *   - `transient` — the silent refresh transiently failed (timeout / 5xx /
 *     exhausted connection retries). Do NOT paint the login shell — hold a
 *     "Reconnecting…" loader + offer a member-initiated retry.
 */
export type BootstrapStatus = 'authed' | 'unauthenticated' | 'transient';

/**
 * The dedicated receiver route that owns the reverse FOS→DAO cross-domain PKCE
 * exchange (bl-1107-a). Its component (`CrossDomainLoginPage`) reads the PKCE
 * verifier and calls `exchangeCodeForJwt(code, verifier)` itself.
 */
const CROSS_DOMAIN_RECEIVER_PATH = '/auth/cross-domain-login';

/** The hash-fragment key carrying the PKCE verifier on a cross-domain handoff (`#v=<verifier>`). */
const VERIFIER_HASH_KEY = 'v';

/**
 * True when the current URL is a reverse cross-domain PKCE handoff that the
 * dedicated {@link CROSS_DOMAIN_RECEIVER_PATH} receiver must own — NOT the
 * front-door `?code=` flow (bl-1107-a).
 *
 * The `?code=` minted by identity-service `/cross-domain/authorize` is
 * PKCE/challenge-bound and can ONLY be redeemed with its `code_verifier` (carried
 * in the `#v=` fragment). This pre-mount bootstrap exchanges a `?code=` WITHOUT a
 * verifier — if it touched a challenge-bound code it would 401 against
 * `/token/exchange` AND strip the single-use code, starving the receiver
 * (→ front-door bounce). This guard makes the bootstrap stand FULLY down so the
 * receiver owns the crossing.
 *
 * Discriminator: the front-door `?code=` flow NEVER carries a `#v=` fragment; the
 * cross-domain PKCE flow ALWAYS does. The receiver-route pathname is a
 * belt-and-suspenders second signal. Only `v=` PRESENCE is tested here, never its
 * value — so the `URLSearchParams` `+`→space decode is irrelevant (the receiver
 * reads the verifier VALUE via a raw split). Mirrors the FOS-side discriminator
 * (`founderyos-dashboard/src/lib/identity-code-bootstrap.ts`).
 */
function isCrossDomainPkceHandoff(): boolean {
  try {
    const pathname = window.location.pathname.toLowerCase();
    const p = CROSS_DOMAIN_RECEIVER_PATH;
    if (pathname === p || pathname.startsWith(p + '/')) return true;
    const rawHash = window.location.hash.replace(/^#/, '');
    if (!rawHash) return false;
    const verifier = new URLSearchParams(rawHash).get(VERIFIER_HASH_KEY);
    return verifier !== null && verifier.length > 0;
  } catch {
    return false;
  }
}

/**
 * True when the current URL carries a front-door one-time `?code=` (login return).
 *
 * `App` now always awaits {@link runIdentityCodeBootstrap} (it handles both the
 * `?code=` exchange and the no-code silent refresh), so this predicate is retained
 * only as a pure URL probe for callers that need to distinguish a login return from
 * a plain reload without running the bootstrap.
 */
export function identityCodeExchangePending(): boolean {
  try {
    return new URLSearchParams(window.location.search).has(CODE_PARAM);
  } catch {
    return false;
  }
}

/**
 * Replace the current history entry with `nextUrl` (no new entry, preserving
 * history state). Used to drop the consumed one-time code from the address bar
 * and/or restore the deep-link path before the router mounts.
 */
function replaceUrl(nextUrl: string): void {
  try {
    window.history.replaceState(window.history.state, '', nextUrl);
  } catch {
    // history API unavailable — non-fatal; the code is single-use + already
    // consumed server-side, so a lingering param is cosmetic only.
  }
}

/** Strip the one-time `code` param from the current URL, preserving path/other params/hash. */
function urlWithoutCode(): string {
  const url = new URL(window.location.href);
  url.searchParams.delete(CODE_PARAM);
  const search = url.searchParams.toString();
  return `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
}

/** Module-singleton in-flight (then settled) bootstrap promise. */
let bootstrapPromise: Promise<BootstrapStatus> | null = null;

/**
 * bl-1247: a REAL settled flag for the `AuthProvider.isInitialSessionResolving`
 * seam — `true` while the singleton bootstrap promise (initial load OR a manual
 * retry) is genuinely in flight, `false` once it settles by EITHER outcome.
 *
 * Deliberately NOT `bootstrapPromise != null` (that stays non-null forever after
 * settling — only {@link resetIdentityCodeBootstrap} clears it — so a predicate
 * reading it would return `true` permanently and park the member on the
 * `ProtectedRoute` fallback with no exit; `failure-as-plausible-value.md` rule 1).
 * This flag is set when the promise is created, cleared in its `.finally` (both
 * fulfil and reject paths), and re-armed by {@link resetIdentityCodeBootstrap} so a
 * manual Retry re-enters the in-flight state.
 */
let bootstrapInFlight = false;

/**
 * bl-1247: `true` while the pre-mount identity bootstrap / silent `/token/refresh`
 * is still resolving. Read by `App.tsx`'s `isInitialSessionResolving` predicate to
 * hold `AuthProvider`'s unauthenticated verdict while a slow-but-succeeding refresh
 * is still in flight (rather than bouncing a member who holds a valid
 * `hwdao_refresh` cookie). Module-scope + stable so the predicate reference passed
 * to `AuthProvider` is stable across re-renders.
 */
export function isIdentityBootstrapInFlight(): boolean {
  return bootstrapInFlight;
}

/**
 * Settle the identity-service session before the auth tree mounts.
 *
 * Two entry paths, one single-flight promise:
 *   - `?code=` present (login return) → exchange the one-time code for a JWT, store
 *     it, and reconcile the URL (restore the bl-714 deep-link path on success,
 *     otherwise strip the consumed code).
 *   - no `?code=` (plain reload / fresh load) → silent refresh from the httpOnly
 *     `hwdao_refresh` cookie; on success store the fresh JWT so the session
 *     rehydrates, on failure proceed unauthenticated.
 *
 * Idempotent + single-flight: repeated calls (incl. StrictMode double-invoke)
 * return the SAME cached promise, so the code is exchanged — or the refresh
 * attempted — exactly once per page load.
 *
 * Resolves (never rejects) once the JWT store + URL are settled, so the caller can
 * mount the auth tree.
 */
export function runIdentityCodeBootstrap(): Promise<BootstrapStatus> {
  if (bootstrapPromise) return bootstrapPromise;

  // bl-1247: mark the session resolution in flight BEFORE the promise runs, and
  // clear it once THIS promise settles. The identity guard (`bootstrapPromise === p`)
  // stops a stale `.finally` — e.g. from a promise superseded by a retry's reset +
  // re-run — from clobbering a newer run's in-flight state.
  bootstrapInFlight = true;
  const p = (async (): Promise<BootstrapStatus> => {
    // bl-1107-a: a reverse cross-domain PKCE handoff (`?code=…#v=<verifier>` on the
    // /auth/cross-domain-login receiver) is owned by the dedicated receiver, which
    // reads the `#v=` verifier and exchanges `{ code, code_verifier }`. This
    // pre-mount bootstrap must stand FULLY down — a verifier-less exchange here
    // would 401 and strip the single-use code (starving the receiver → front-door
    // bounce), and a silent refresh would compete for the cookie. Return
    // `unauthenticated` (fail-safe: no session established by the bootstrap); the
    // receiver establishes the JWT and the member lands authed on navigation. This
    // short-circuits BEFORE the code/refresh paths below.
    if (isCrossDomainPkceHandoff()) return 'unauthenticated';

    let code: string | null = null;
    try {
      code = new URLSearchParams(window.location.search).get(CODE_PARAM);
    } catch {
      code = null;
    }

    // Plain reload / fresh load (no login-return code): the in-memory JWT store is
    // empty after a refresh, but the httpOnly refresh cookie may still be valid.
    // Attempt a BOUNDED silent refresh to rehydrate the session (bl-977). No URL
    // reconciliation here: there is no code to strip and no deep link to restore
    // (the address bar already holds the path the member is on).
    //
    // The outcome is classified, NOT collapsed to JWT-or-null: only a definitive
    // 401 (`unauthenticated`) may paint the login shell; a timeout / 5xx / exhausted
    // connection-retry is `transient` and the caller holds a loader instead of
    // bouncing a valid-cookie member to sign-in.
    if (!code) {
      const outcome = await refreshIdentityJwtBounded();
      if (outcome.status === 'authenticated') {
        setIdentityJwt(outcome.value);
        return 'authed';
      }
      return outcome.status === 'unauthenticated' ? 'unauthenticated' : 'transient';
    }

    const jwt = await exchangeCodeForJwt(code);
    if (jwt) {
      setIdentityJwt(jwt);
      // bl-714: on a successful exchange (now authed via the JWT seam), restore the
      // deep path the member requested before the front-door redirect. The stashed
      // path never contains the code, so it needs no separate strip.
      const returnPath = takeDeepLinkReturnPath();
      if (returnPath) {
        replaceUrl(returnPath);
      } else {
        // No deep link: strip the consumed one-time code from the address bar.
        replaceUrl(urlWithoutCode());
      }
      return 'authed';
    }
    // Exchange failed (invalid / used / expired code): the code is single-use +
    // already consumed server-side and must never linger. There is no cookie
    // fallback (-7b), so a failed exchange is a definitive signed-out state.
    replaceUrl(urlWithoutCode());
    return 'unauthenticated';
  })();

  bootstrapPromise = p;
  void p
    .finally(() => {
      if (bootstrapPromise === p) bootstrapInFlight = false;
    })
    // The bootstrap is designed to resolve, never reject (refreshIdentityJwtBounded /
    // exchangeCodeForJwt swallow their own errors). This `.catch` is only so the
    // flag-clearing chain never surfaces as an unhandled rejection in the
    // pathological case where an underlying call DOES throw — real callers still
    // receive (and handle) the original `p`.
    .catch(() => {});

  return bootstrapPromise;
}

/**
 * Reset the single-flight bootstrap promise.
 *
 * Two callers: (1) tests, between cases; (2) the App-level `transient`-retry
 * (bl-977) — after a transiently-failed silent refresh, the member's manual
 * "Retry" resets the settled singleton so the next `runIdentityCodeBootstrap`
 * re-attempts. MUST NOT be called on the initial mount (that would defeat the
 * StrictMode single-flight guarantee); it is only invoked on an explicit retry.
 */
export function resetIdentityCodeBootstrap(): void {
  bootstrapPromise = null;
  // bl-1247: clear the in-flight flag — resolution is momentarily idle. A manual
  // Retry re-enters the "session resolving" state on its very next
  // runIdentityCodeBootstrap() call (which sets the flag before its promise runs),
  // so the flag faithfully tracks "a bootstrap promise is pending" with no window
  // where it reads `true` without a live promise behind it.
  bootstrapInFlight = false;
}
